import { z } from 'zod';
import { NameSchema, about, sourceOf } from './schema_fields.ts';

// What a tap on a tile does, for tiles without a tap of their own (tiles spec §6, TA-1 to TA-8,
// issue #31): go to a screen, or send a command to one of the tile's data sources, as the action
// tile does. Every tile but a group may have one (`tap` in the tile base, `dashboard.ts`).

/** Goes to the dashboard's screen of that name (TA-5); a name it does not have: nothing. */
export const ScreenTapSchema = z
    .object({
        kind: z.literal('screen'),
        screen: z.string().min(1).meta(about('Screen', 'the screen a tap goes to')),
    })
    .meta({ title: 'Go to a screen' });

/** Sends a command, with an optional argument, to one of the tile's data sources (`tap` when the editor makes it). */
export const CommandTapSchema = z
    .object({
        kind: z.literal('command'),
        source: sourceOf(NameSchema, { gives: [], commands: true, label: 'Device' }),
        command: z.string().min(1).meta(about('Command', 'what a tap sends to the device', 'command')),
        argument: z.union([z.string(), z.number(), z.boolean()]).optional().meta(about('Argument', 'sent with the command, if it takes one', 'command-argument')),
    })
    .meta({ title: 'Send a command' });

export const TileTapSchema = z.discriminatedUnion('kind', [ScreenTapSchema, CommandTapSchema]);

export type TileTap = z.output<typeof TileTapSchema>;
export type TileTapKind = TileTap['kind'];

let commandTapJson: Record<string, unknown> | undefined;

/** The command tap as JSON Schema, for the editor's form of it (TA-6), as `tileOptionsJsonSchema` gives a tile's options. */
export function commandTapJsonSchema() : Record<string, unknown> {
    commandTapJson ??= z.toJSONSchema(CommandTapSchema, { io: 'input', unrepresentable: 'any' }) as Record<string, unknown>;

    return commandTapJson;
}

/** The name the editor gives the data source of a tap's command, with a number when it is taken. */
export const TAP_SOURCE = 'tap';

/** What a tile needs to say whether it has a tap of its own: its type, and a status tile's command. */
interface TapTile {
    readonly type: string;
    readonly options?: unknown;
    readonly tap?: TileTap | undefined;
}

/** Tile types whose tap does something of their own (TA-3); a status tile only with a command. */
const OWN_TAP_TYPES: ReadonlySet<string> = new Set(['light', 'switch', 'action', 'thermostat', 'video', 'image', 'graph']);

/** Whether a tile's tap is its type's own (TA-3), so that a `tap` is ignored and not offered. */
export function hasOwnTap(tile: TapTile) : boolean {
    if (tile.type === 'status') {
        const options = tile.options as { command?: unknown } | undefined;

        return typeof options?.command === 'string';
    }

    return tile.type === 'group' || OWN_TAP_TYPES.has(tile.type);
}

/** The tap that applies to the tile: its `tap`, unless its type has a tap of its own (TA-3). */
export function tapOf(tile: TapTile) : TileTap | undefined {
    return hasOwnTap(tile) ? undefined : tile.tap;
}
